import * as React from "react";
import { cn } from "@/lib/utils";

interface NumberInputProps {
  value: number | null | undefined;
  onChange: (value: number | null) => void;
  // allowEmpty=true: campo vazio vira null (ex.: maxKm "∞"). false: vazio emite
  // `emptyValue` (default 0) pro pai mas o input fica VISUALMENTE vazio enquanto
  // se digita — senão o pai re-renderiza "0" em cima e o usuário não consegue
  // apagar pra digitar outro número (o 0 "renasce" e o dígito gruda: 0 -> 04).
  allowEmpty?: boolean;
  emptyValue?: number;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  min?: number;
  step?: number;
  id?: string;
  // Mostra a casa decimal com vírgula («3,2»), como o lojista digita. Sem ela, o número volta com ponto ao sair do campo.
  decimalComma?: boolean;
}

const NumberInput = React.forwardRef<HTMLInputElement, NumberInputProps>(
  (
    { value, onChange, allowEmpty = false, emptyValue = 0, placeholder, className, disabled, min, step, id, decimalComma = false },
    ref,
  ) => {
    const [displayValue, setDisplayValue] = React.useState("");
    const mostra = (v: number | null | undefined) =>
      v === null || v === undefined ? "" : decimalComma ? String(v).replace(".", ",") : String(v);
    // Impede o effect de resincronizar o display quando a mudança de `value`
    // veio do nosso próprio onChange (mesmo truque do CurrencyInput).
    const internalChange = React.useRef(false);

    React.useEffect(() => {
      if (internalChange.current) {
        internalChange.current = false;
        return;
      }
      setDisplayValue(mostra(value));
    }, [value]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      // Aceita vazio, dígitos e UM separador decimal (vírgula ou ponto).
      if (raw !== "" && !/^\d*[.,]?\d*$/.test(raw)) return;
      internalChange.current = true;
      setDisplayValue(raw);
      if (raw === "") {
        onChange(allowEmpty ? null : emptyValue);
        return;
      }
      const n = parseFloat(raw.replace(",", "."));
      // raw só com separador ("." / ",") ainda não é número: mantém o texto e
      // não emite (deixa o usuário continuar pra "0.5" etc).
      if (!Number.isNaN(n)) onChange(n);
    };

    const handleBlur = () => {
      // Normaliza o display no blur: "" -> valor canônico, "04" -> "4".
      setDisplayValue(mostra(value));
    };

    return (
      <input
        ref={ref}
        id={id}
        type="text"
        inputMode="decimal"
        value={displayValue}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        min={min}
        step={step}
        className={cn(
          "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
          className,
        )}
      />
    );
  },
);

NumberInput.displayName = "NumberInput";

export { NumberInput };
